import { handleThunkError } from "@/utils/thunkErrorHandler";
import { createAsyncThunk } from "@reduxjs/toolkit";
import axiosInstance, { isAxiosError } from "@/utils/axiosInstance";
import { Params } from "@/types";

const endpoint = {
  base: "/subscriptions",
};

export const changePlan = createAsyncThunk(
  "subscriptions/changePlan",
  async (
    {
      clientSubscriptionId,
      subscriptionId,
      clientId,
    }: {
      clientSubscriptionId: string;
      subscriptionId: string;
      clientId: string;
    },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.put(
        `/subscriptions/${subscriptionId}/clients/${clientSubscriptionId}/change-plan`,
        { subscription_id: subscriptionId, client_id: clientId },
      );
      return response.data;
    } catch (error) {
      return handleThunkError(error, thunkAPI, "Erro ao alterar plano");
    }
  },
);
// --- SubscriptionConfig thunks ---
export const fetchSubscriptionConfig = createAsyncThunk(
  "subscriptionConfig/fetchSubscriptionConfig",
  async ({ establishment }: { establishment: string }, thunkAPI) => {
    try {
      const response = await axiosInstance.get(`/subscriptions/config`);
      return response.data;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        // Se não encontrar configuração (404), retornar null ao invés de erro
        return null;
      }
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao buscar configuração de assinatura",
        });
      }
    }
  },
);

export const saveSubscriptionConfig = createAsyncThunk(
  "subscriptionConfig/saveSubscriptionConfig",
  async (
    { establishment, data }: { establishment: string; data: any },
    thunkAPI,
  ) => {
    try {
      const url = `/subscriptions/config`;

      const response = await axiosInstance.post(url, data);
      return response.data;
    } catch (error) {
      console.error("Erro na requisição:", error);
      if (isAxiosError(error)) {
        console.error("Status:", error.response?.status);
        console.error("Data:", error.response?.data);
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao salvar configuração de assinatura",
        });
      }
    }
  },
);

export const fetchSubscriptions = createAsyncThunk(
  "subscription/fetchSubscriptions",
  async ({ params }: { params: Params }, thunkAPI) => {
    try {
      const response = await axiosInstance.get(`${endpoint.base}`, {
        params,
      });
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao buscar assinaturas de cliente",
        });
      }
    }
  },
);

export const fetchSubscriptionsAll = createAsyncThunk(
  "subscription/fetchSubscriptionsAll",
  async (
    params: Omit<Params, "current_page" | "per_page"> & {
      without_pagination: 1;
    },
    thunkAPI,
  ) => {
    try {
      // Fazer requisição direta sem cache - apenas manter na sessão
      const response = await axiosInstance.get(`${endpoint.base}`, {
        params,
      });
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao buscar todas as assinaturas",
        });
      }
    }
  },
);

export const createSubscription = createAsyncThunk(
  "subscription/createSubscription",
  async (
    { subscription_id, data }: { subscription_id: string; data: any },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.post(
        `${endpoint.base}/${subscription_id}/clients`,
        data,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao criar cliente da assinatura",
        });
      }
    }
  },
);

export const updateSubscription = createAsyncThunk(
  "subscription/updateSubscription",
  async (
    {
      subscription_id,
      slug,
      data,
    }: { subscription_id: string; slug: string; data: any },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.put(
        `${endpoint.base}/${subscription_id}/clients/${slug}`,
        data,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao atualizar cliente da assinatura",
        });
      }
    }
  },
);

export const deleteSubscription = createAsyncThunk(
  "subscription/deleteSubscription",
  async (
    { subscription_id, slug }: { subscription_id: string; slug: string },
    thunkAPI,
  ) => {
    try {
      const response = await axiosInstance.delete(
        `${endpoint.base}/${subscription_id}/clients/${slug}`,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao excluir cliente da assinatura",
        });
      }
    }
  },
);

export const updateSubscriptionUsage = createAsyncThunk(
  "subscription/updateSubscription",
  async (
    {
      subscription_id,
      slug,
      userId,
      dateTicket,
      schedule_Id,
      client_subscription_id,
      data,
    }: {
      subscription_id: string;
      slug: string;
      userId: string;
      dateTicket: string;
      schedule_Id: string;
      client_subscription_id: string;
      data: any;
    },
    thunkAPI,
  ) => {
    try {
      // console.log(subscription_id, slug, data, dateTicket, userId, subscription_id)

      const payload = {
        itens: data,
        userId,
        dateTicket,
        subscription_id,
        schedule_Id,
        client_subscription_id,
      };

      const response = await axiosInstance.post(
        `${endpoint.base}/${subscription_id}/clients/${slug}/usage`,
        payload,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao atualizar cliente da assinatura",
        });
      }
    }
  },
);

export const fetchSubscriptionSummary = createAsyncThunk(
  "subscriptionConfig/fetchSubscriptionSummary",
  async (_, thunkAPI) => {
    try {
      const response = await axiosInstance.get(
        `/subscriptions/subscribers/summary`,
      );
      return response.data;
    } catch (error) {
      if (isAxiosError(error) && error.response?.status === 404) {
        // Se não encontrar configuração (404), retornar null ao invés de erro
        return null;
      }
      if (isAxiosError(error)) {
        return thunkAPI.rejectWithValue({ error: error.message });
      } else {
        return thunkAPI.rejectWithValue({
          error: "Erro desconhecido ao buscar configuração de assinatura",
        });
      }
    }
  },
);
